import React, { useState, useEffect } from 'react';
import axios from 'axios';
import "../rest/UserManagement.css";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '1200px',
  margin: '0 auto',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  backgroundColor: '#f9fafc',
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const UserCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
  borderLeft: '4px solid #3f51b5',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#ff4444',
  color: 'white',
  '&:hover': {
    backgroundColor: '#cc0000',
  },
}));

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Стилі для адаптивності
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f0f2f5',
      padding: { xs: '16px', sm: '24px' },
    },
    tableContainer: {
      overflowX: 'auto',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    },
    table: {
      minWidth: { xs: '600px', sm: '800px' },
    },
    tableHeader: {
      backgroundColor: '#3f51b5',
      '& th': {
        color: 'white',
        fontWeight: 'bold',
        fontSize: { xs: '0.875rem', sm: '1rem' },
      },
    },
    tableRow: {
      '&:nth-of-type(odd)': {
        backgroundColor: '#f8f9fa',
      },
      '&:hover': {
        backgroundColor: '#e3f2fd',
      },
    },
    actionCell: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    },
    statsCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      textAlign: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('https://hub2.daolog.net/users-admin');
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      setError('Помилка при завантаженні користувачів');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      // Виклик нового ендпоінта для видалення
      await axios.delete(`https://hub2.daolog.net/users/${selectedUser.id}`);
      
      // Оновлення локального стану
      setUsers(prevUsers => prevUsers.filter(user => user.id !== selectedUser.id));
      
      setSuccess(`Користувача "${selectedUser.username}" успішно видалено`);
      setSelectedUser(null);
      setDeleteDialogOpen(false);
      
      // Автоматичне приховування повідомлення про успіх
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Помилка при видаленні користувача');
    }
  };

  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не вказано';
    const date = new Date(dateString);
    return date.toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStats = () => {
    return {
      total: users.length,
      active: users.filter(u => u.status !== 'inactive').length,
      withContractors: users.filter(u => u.contractor_id).length,
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <StyledContainer maxWidth="lg">
      {(error || success) && (
        <Alert 
          severity={error ? "error" : "success"} 
          sx={{ mb: 3 }}
          onClose={() => error ? setError('') : setSuccess('')}
        >
          {error || success}
        </Alert>
      )}

      <StyledPaper>
        <HeaderBox>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#2c3e50' }}>
              <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Управління користувачами
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Всього користувачів: {getStats().total}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Пошук за іменем або email..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: '300px' } }}
            />
            <Tooltip title="Оновити список">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchUsers}
              >
                Оновити
              </Button>
            </Tooltip>
          </Box>
        </HeaderBox>

        {/* Статистика */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          <Paper sx={styles.statsCard}>
            <Typography variant="h6" color="primary">{getStats().total}</Typography>
            <Typography variant="body2">Всього користувачів</Typography>
          </Paper>
          <Paper sx={styles.statsCard}>
            <Typography variant="h6" color="success.main">{getStats().active}</Typography>
            <Typography variant="body2">Активних</Typography>
          </Paper>
          <Paper sx={styles.statsCard}>
            <Typography variant="h6" color="info.main">{getStats().withContractors}</Typography>
            <Typography variant="body2">З контрагентами</Typography>
          </Paper>
        </Box>

        {/* Таблиця користувачів */}
        <TableContainer component={Paper} sx={styles.tableContainer}>
          <Table sx={styles.table}>
            <TableHead sx={styles.tableHeader}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Користувач</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Регіон</TableCell>
                <TableCell>Дата створення</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="center">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchTerm ? 'Користувачів не знайдено' : 'Користувачів немає'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} sx={styles.tableRow}>
                    <TableCell>{user.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="action" />
                        <Typography variant="body2" fontWeight="medium">
                          {user.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.region || 'Не вказано'}</TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.5,
                          borderRadius: '4px',
                          backgroundColor: user.status === 'active' ? '#e8f5e9' : '#ffebee',
                          color: user.status === 'active' ? '#2e7d32' : '#c62828',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {user.status || 'active'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={styles.actionCell}>
                        <Tooltip title="Видалити">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(user)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Інформація про кількість */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Показано {filteredUsers.length} з {users.length} користувачів
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm && `Результати пошуку: "${searchTerm}"`}
          </Typography>
        </Box>
      </StyledPaper>

      {/* Діалог підтвердження видалення */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Підтвердження видалення</DialogTitle>
        <DialogContent>
          <Typography>
            Ви дійсно хочете видалити користувача <strong>{selectedUser?.username}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            ⚠️ Ця дія видалить користувача та всі пов'язані з ним дані. Дію неможливо скасувати!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Скасувати
          </Button>
          <DeleteButton onClick={handleDeleteConfirm} variant="contained">
            Видалити
          </DeleteButton>
        </DialogActions>
      </Dialog>
    </StyledContainer>
  );
};

export default UserManagement;